import { isNumeric } from "app/frontend/utils";

// https://stackoverflow.com/a/16233919/6410635
const CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

const DECIMAL_CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

// Abbreviates thousands as "K" with one decimal (e.g. $1.5K) for space-constrained chart axes.
const COMPACT_CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  notation: "compact",
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 1,
});

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#decimal_and_percent_formatting
const PERCENT_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "percent",
});

/**
 * Parses a dollar value (string or number) into a Number.
 *
 * Strips currency/grouping characters (e.g. `$`, `,`, whitespace) while
 * preserving the decimal point and a leading minus sign, so floats and
 * negatives parse correctly. Returns `NaN` for non-numeric input.
 *
 * Examples:
 *   parseDollars("$1,234.56") // 1234.56
 *   parseDollars("30000.0")   // 30000
 *   parseDollars(490.38)      // 490.38
 *   parseDollars(null)        // NaN
 */
export const parseDollars = (
  value: string | number | null | undefined,
): number => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return NaN;

  // Replace everything except digits or .
  return Number(value.replace(/[^\d.-]/g, ""));
};

export const asDollars = (
  amount: number | string | null | undefined,
): string => {
  const n = parseDollars(amount);
  if (!isNumeric(amount) || isNaN(n)) return "$";

  const digits = amount!.toString().includes(".") ? 2 : 0;

  return digits
    ? DECIMAL_CURRENCY_FORMATTER.format(n)
    : CURRENCY_FORMATTER.format(n);
};

// Compact dollar format for tight displays: full under $1,000, "K"-abbreviated above ($1.5K, $49K).
export const asCompactDollars = (
  amount: number | string | null | undefined,
): string => {
  const n = parseDollars(amount);
  if (!isNumeric(amount) || isNaN(n)) return "$";

  return COMPACT_CURRENCY_FORMATTER.format(n);
};

export const asPercent = (amount: number | null): string => {
  if (!amount) return "";
  return PERCENT_FORMATTER.format(amount / 100);
};
